import { useEffect, useState, useCallback } from "react";

const THEME_EVENT = "lumifit:theme-change";
const STORAGE_KEY = "theme";

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

const applyTheme = (theme) => {
  const html = document.documentElement;
  if (theme === "dark") html.classList.add("dark");
  else html.classList.remove("dark");
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch { /* storage disabled */ }
};

/**
 * useTheme — single source of truth for light/dark mode across the app.
 * All toggle buttons subscribe to the same custom event, so icons + classes
 * stay in perfect sync no matter which toggle the user clicks.
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState(getStoredTheme);

  useEffect(() => {
    // Sync DOM on mount in case the pre-boot script + stored value diverged.
    applyTheme(theme);

    const onChange = (e) => setThemeState(e.detail);
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setThemeState(getStoredTheme());
    };
    window.addEventListener(THEME_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(THEME_EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setTheme = useCallback((next) => {
    applyTheme(next);
    setThemeState(next);
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: next }));
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, isDark: theme === "dark", setTheme, toggle };
};
